import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Edit2 } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function UsersManagement({ users, onRefresh }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const handleUpdateSubscription = async (userId, plan, status, mode) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/admin/users/${userId}/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription_plan: plan,
          subscription_status: status,
          app_mode: mode
        })
      });
      
      if (response.ok) {
        toast.success('Subscription updated');
        onRefresh();
        setEditingUser(null);
      } else {
        toast.error('Failed to update subscription');
      }
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(user =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [users, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">User Subscriptions</h2>
          <p className="text-slate-500 text-sm mt-1">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-sm text-slate-600">Per page:</Label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
          >
            {ITEMS_PER_PAGE_OPTIONS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="pl-10"
            />
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">User</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Plan</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Mode</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Status</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Created</th>
              <th className="text-right px-6 py-3 text-sm font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {paginatedUsers.map(u => (
              <tr key={u.user_id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-slate-900">{u.name}</p>
                    <p className="text-sm text-slate-500">{u.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {editingUser === u.user_id ? (
                    <select
                      defaultValue={u.subscription_plan}
                      onChange={(e) => {
                        const newPlan = e.target.value;
                        const newMode = newPlan === 'crm' ? 'crm' : 'standard';
                        handleUpdateSubscription(u.user_id, newPlan, u.subscription_status, newMode);
                      }}
                      className="h-9 rounded-lg border px-2 text-sm"
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="business">Business</option>
                      <option value="crm">CRM</option>
                    </select>
                  ) : (
                    <span className="text-sm text-slate-700 capitalize">{u.subscription_plan}</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-slate-700 capitalize">{u.app_mode}</span>
                </td>
                <td className="px-6 py-4">
                  {editingUser === u.user_id ? (
                    <select
                      defaultValue={u.subscription_status}
                      onChange={(e) => {
                        handleUpdateSubscription(u.user_id, u.subscription_plan, e.target.value, u.app_mode);
                      }}
                      className="h-9 rounded-lg border px-2 text-sm"
                    >
                      <option value="active">Active</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      u.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                      u.subscription_status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {u.subscription_status}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingUser(editingUser === u.user_id ? null : u.user_id)}
                    className="rounded-lg"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
